import { useMemo } from 'react'
import { QuadraticBezierCurve3, Vector3 } from 'three'

import type { Direction, GridConfig, TrackShape } from '@config'
import { DIRS } from '@engine'
import { useGameStore } from '@stores'
import {
  DIRECTION_OFFSETS,
  getShapeTexture,
  getWorldPosition,
  sampleSplines,
  SPLINE_HEIGHT,
  SPLINES_COUNT,
} from '@utils'

function isDecoration(cell: GridConfig[number][number]) {
  return cell && typeof cell === 'object' && !Array.isArray(cell) && cell.decoration
}

function getGridSize(grid: GridConfig) {
  const fullWidth = grid.length ? Math.max(...grid.map(row => row.length)) : 0
  const height = grid.length

  const rowBounds = grid
    .map(row => {
      const first = row.findIndex(cell => cell && !isDecoration(cell))
      if (first === -1) return null
      const last = row.length - 1 - [...row].reverse().findIndex(cell => cell && !isDecoration(cell)) //prettier-ignore
      return { first, last }
    })
    .filter(b => b !== null)

  const leftEdge = rowBounds.length ? Math.min(...rowBounds.map(b => b.first)) : 0
  const rightEdge = rowBounds.length ? Math.max(...rowBounds.map(b => b.last)) : 0
  const visibleWidth = rowBounds.length ? rightEdge - leftEdge + 1 : 0
  const centerOffset = leftEdge + (visibleWidth - 1) / 2 - (fullWidth - 1) / 2

  let exitSide: 'W' | 'E' | undefined
  outer: for (const row of grid) {
    for (const cell of row) {
      if (
        cell &&
        typeof cell === 'object' &&
        !Array.isArray(cell) &&
        (cell.exit === 'W' || cell.exit === 'E')
      ) {
        exitSide = cell.exit
        break outer
      }
    }
  }

  return { visibleWidth, fullWidth, height, centerOffset, exitSide }
}

function getCurve(shape: TrackShape) {
  const center = new Vector3(0, 0, 0)

  if (shape.length === 1) {
    const bumperDir = shape as Direction
    const openDir = DIRS[bumperDir].opposite

    return new QuadraticBezierCurve3(
      DIRECTION_OFFSETS[openDir],
      center,
      DIRECTION_OFFSETS[bumperDir].clone().multiplyScalar(0.5),
    )
  }

  const startDir = shape[0] as Direction
  const endDir = shape[1] as Direction
  return new QuadraticBezierCurve3(DIRECTION_OFFSETS[startDir], center, DIRECTION_OFFSETS[endDir])
}

export interface CellGeometry {
  position: Vector3
  curves: QuadraticBezierCurve3[]
}

export interface SplineInstanceData {
  position: Vector3
  rotationY: number
  isSwitch: boolean
}

export interface RailInstanceData {
  curve: QuadraticBezierCurve3
  cellPosition: Vector3
  active: boolean
}

export interface BallastInstanceData {
  worldPos: Vector3
  textureName: string
  rotationDeg: number
  scale?: number
  positionYOffset?: number
}

export function useLevelGeometry() {
  const grid = useGameStore(state => state.level.grid)
  const network = useGameStore(state => state.network)
  const size = useMemo(() => getGridSize(grid ?? []), [grid])

  const cellGeometries = useMemo(() => {
    const map = new Map<string, CellGeometry>()
    for (const cell of network.cells.values()) {
      const cellWorldPos = getWorldPosition(cell.x, cell.y, size.fullWidth, size.height)
      const curves = cell.shapes.map(getCurve)
      map.set(cell.id, { position: cellWorldPos, curves })
    }
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grid, size])

  const splineInstances = useMemo(() => {
    const instances: SplineInstanceData[] = []
    for (const cell of network.cells.values()) {
      const isBumper = cell.type === 'bumper'
      const isSwitch = cell.type === 'switch'
      const cellWorldPos = getWorldPosition(cell.x, cell.y, size.fullWidth, size.height)

      for (const curve of cell.shapes.map(getCurve)) {
        const count = isBumper ? SPLINES_COUNT - 1 : SPLINES_COUNT
        const poses = sampleSplines(curve, count)

        poses.forEach((pose, i) => {
          const isFirst = i === 0
          const isLast = i === poses.length - 1

          if (isSwitch && (isFirst || isLast)) return
          if (isBumper && isLast) return

          instances.push({
            position: new Vector3(
              cellWorldPos.x + pose.position.x,
              SPLINE_HEIGHT * 0.5 + pose.position.y,
              cellWorldPos.z + pose.position.z,
            ),
            rotationY: pose.rotationY,
            isSwitch,
          })
        })
      }
    }
    return instances
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grid, size])

  const railInstances = useMemo(() => {
    const instances: RailInstanceData[] = []
    for (const cell of network.cells.values()) {
      const isSwitch = cell.type === 'switch'
      const cellWorldPos = getWorldPosition(cell.x, cell.y, size.fullWidth, size.height)
      for (const curve of cell.shapes.map(getCurve)) {
        instances.push({ curve, cellPosition: cellWorldPos, active: !isSwitch })
      }
    }
    return instances
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grid, size])

  const ballastInstances = useMemo(() => {
    const instances: BallastInstanceData[] = []
    for (const cell of network.cells.values()) {
      const worldPos = getWorldPosition(cell.x, cell.y, size.fullWidth, size.height)
      const {
        texture: textureName,
        rotationY: rotationDeg,
        scale,
        positionYOffset,
      } = getShapeTexture(cell.shapes)
      instances.push({ worldPos, textureName, rotationDeg, scale, positionYOffset })
    }
    return instances
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grid, size])

  return { cellGeometries, splineInstances, railInstances, ballastInstances, size }
}
